'use client';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { animated, useTransition } from 'react-spring';
import { GRUND, hexTillRgba, ljus, type Kombonamn } from './rörelse-konfig';
import { useRörelse } from './RörelseKontext';
import { SPRING } from './rörelse-konfig';
import type { IFärgKombo } from 'src/theme/NyttFärgtema';

const AnimeradDiv = animated('div');

export type Växelscen = { nyckel: string; etikett: string; innehåll: ReactNode };

type Props = {
    scener: Växelscen[];
    kombo: Kombonamn | IFärgKombo;
    /** Millisekunder mellan automatiska byten. Ignoreras helt vid mindre rörelse. */
    auto?: number;
    höjd?: number | string;
    etikett: string;
};

/**
 * Byter mellan lägen av samma sak.
 *
 * Vid mindre rörelse slutar den avancera av sig själv och blir enbart manuell — en
 * karusell som byter innehåll utan att man bett om det är precis vad den inställningen
 * ber att slippa. Piltangenter och punkter fungerar i båda lägena.
 */
export function ScenVäxlare({ scener, kombo, auto, höjd = 260, etikett }: Props) {
    const { mindreRörelse } = useRörelse();
    const [index, sättIndex] = useState(0);
    // Riktningen styr hur övergången ritas och är alltså renderingsdata. Som ref hade
    // den lästs under render, vilket React uttryckligen varnar för.
    const [riktning, sättRiktning] = useState<1 | -1>(1);

    const gåTill = useCallback(
        (nytt: number) => {
            sättRiktning(nytt > index || (index === scener.length - 1 && nytt === 0) ? 1 : -1);
            sättIndex(((nytt % scener.length) + scener.length) % scener.length);
        },
        [index, scener.length]
    );

    useEffect(() => {
        if (!auto || mindreRörelse || scener.length < 2) return undefined;
        const id = window.setInterval(() => {
            sättRiktning(1);
            sättIndex((i) => (i + 1) % scener.length);
        }, auto);
        return () => window.clearInterval(id);
    }, [auto, mindreRörelse, scener.length]);

    const övergångar = useTransition(index, {
        key: index,
        from: { opacity: 0, x: riktning * 56 },
        enter: { opacity: 1, x: 0 },
        leave: { opacity: 0, x: riktning * -40 },
        config: SPRING.snärtig,
        exitBeforeEnter: false
    });

    return (
        <Box
            role="group"
            aria-roledescription="lägesväxlare"
            aria-label={etikett}
            onKeyDown={(e) => {
                if (e.key === 'ArrowRight') gåTill(index + 1);
                if (e.key === 'ArrowLeft') gåTill(index - 1);
            }}
            tabIndex={0}
            sx={{
                outline: 'none',
                '&:focus-visible': { boxShadow: `0 0 0 2px ${ljus(kombo)}`, borderRadius: 1 }
            }}
        >
            <Box sx={{ position: 'relative', height: höjd }}>
                {övergångar((stil, i) => (
                    <AnimeradDiv style={{ ...stil, position: 'absolute', inset: 0, willChange: 'transform, opacity' }}>
                        {scener[i].innehåll}
                    </AnimeradDiv>
                ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                {scener.map((scen, i) => (
                    <ButtonBase
                        key={scen.nyckel}
                        onClick={() => gåTill(i)}
                        aria-current={i === index}
                        sx={{
                            px: 1.75,
                            py: 0.75,
                            borderRadius: 999,
                            fontSize: 13,
                            letterSpacing: '0.01em',
                            color: i === index ? GRUND.botten : GRUND.dämpad,
                            bgcolor: i === index ? ljus(kombo) : 'transparent',
                            border: `1px solid ${i === index ? ljus(kombo) : hexTillRgba(ljus(kombo), 0.28)}`,
                            transition: 'color .18s, background-color .18s, border-color .18s',
                            '&:hover': { borderColor: ljus(kombo), color: i === index ? GRUND.botten : GRUND.text },
                            '&:focus-visible': { outline: `2px solid ${ljus(kombo)}`, outlineOffset: 2 }
                        }}
                    >
                        {scen.etikett}
                    </ButtonBase>
                ))}
            </Box>
        </Box>
    );
}
